import { useMutation, useQuery, } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { DEFAULT_REFRESH_RATE } from "../constants/fallbacks";
import { Actions } from "../store";
import { useWalletContext } from "../store/context";
import { MappedAssets } from "../utils/classes/MappedAssets";
import { displayError } from "../utils/displayError";
import { getArrayLength, getQuantityValues } from "../utils/helpers";
import { useHeartbeatLogger } from "./useHeartbeatLogger";
import { useWalletApi } from "./useWalletApi";
import { useWalletState } from "./useWalletState";
const HOOK_CONTEXT = "useWalletBalance";
export const BALANCE_MUTATION_KEY = "cardano.balance";
export const useWalletBalanceMutation = () => {
    const { state, dispatch } = useWalletContext();
    const hb = useHeartbeatLogger();
    const query = useMutation([BALANCE_MUTATION_KEY], async ({ signal, api }) => {
        hb.logCheckingWallet(HOOK_CONTEXT);
        if (!api) {
            hb.logNoApi(HOOK_CONTEXT);
            return null;
        }
        const [apiBalance, apiUtxos] = await Promise.all([
            api.getBalance(),
            api.getUtxos(),
        ]);
        const freshBalance = await MappedAssets.getMapFromApiBalance({
            apiBalance,
            apiUtxos,
            metadataResolver: state?.options?.metadataResolver,
            ignoreNFTs: state?.options.ignoreNFTs,
            useHandles: state?.options.useHandles,
            network: state?.details?.network,
        });
        if (signal?.aborted) {
            hb.logOperationAborted(HOOK_CONTEXT);
            return null;
        }
        return freshBalance;
    }, {
        onSuccess: (newBalance) => {
            const balanceHasChanged = newBalance &&
                (!isEqual(getArrayLength(newBalance), getArrayLength(state.balance)) ||
                    !isEqual(getQuantityValues(newBalance), getQuantityValues(state.balance)));
            if (!balanceHasChanged) {
                hb.logDoneWithoutChanges(HOOK_CONTEXT);
                return;
            }
            Actions.setBalance(newBalance, dispatch);
            hb.logDoneWithChanges(HOOK_CONTEXT);
        },
        onSettled() {
            if (!state.initialized.balance) {
                Actions.setInitialized({ balance: true }, dispatch);
            }
        },
    });
    return query;
};
export const useWalletBalance = (options) => {
    const { loaded } = useWalletState();
    const { state, dispatch } = useWalletContext();
    const { mutateAsync } = useWalletBalanceMutation();
    const { api } = useWalletApi();
    const query = useQuery([BALANCE_MUTATION_KEY, api], async ({ signal }) => mutateAsync({ signal, api }), {
        refetchOnWindowFocus: "always",
        refetchInterval: state.options?.refreshRates?.balance || DEFAULT_REFRESH_RATE,
        refetchOnMount: false,
        onError: async (e) => {
            console.log(e);
            if (e?.code === -4) {
                return;
            }
            Actions.setInitialized({ balance: false }, dispatch);
            Actions.setError(await displayError(e), dispatch);
        },
        ...options,
    });
    return {
        balance: query.data,
        query,
        isFetched: query.isSuccess,
        isLoaded: loaded.balance,
    };
};
//# sourceMappingURL=useWalletBalance.js.map